import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { FileInput } from "components/FileInput/FileInput";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Field, FieldProps, FormikValues } from "formik";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import {
  getSelectedState,
  initialRegistrationDataType,
  paymentScheduleOption,
  StatusMap,
} from "./SalesTaxRegistrations";
import { addExistingRegistrationValidation } from "formValidations/addExistingRegistration";
import { ToolTipIcon } from "components/icons/TooltipIcon";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import {
  PostSalesRegistrationPayload,
  useCreatePostSalesRegistrationsMutation,
  useGetSalesTaxRegistrationDetailsQuery,
  useUpdatePostSalesTaxRegistrationsMutation,
} from "store/apis/salesTaxRegistration";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { LocalFilePreview } from "components/PreviewModal";
import { useState } from "react";
import {
  IN_REVIEW,
  NONE,
  PENDING_DETAILS,
  PROCESSING,
  REGISTERED,
} from "constants/salesTaxRegistrationStatusType";
import { addRegistrationAdminSchema } from "formValidations/addRegistrationSchema";
import { paymentScheduleMap } from "./RegistrationDetails";
import { DISPLAY_DATE } from "constants/date";

export const statusOptions = [
  { label: "Pending details", value: PENDING_DETAILS },
  { label: "In review", value: IN_REVIEW },
  { label: "Processing", value: PROCESSING },
  { label: "Registered", value: REGISTERED },
];

export const AddExistingRegistrationModal = ({
  onClose,
  stateOptions,
  initialRegistrationData,
}: {
  onClose: () => void;
  stateOptions?: { label: string; value: string }[];
  initialRegistrationData?: initialRegistrationDataType;
}) => {
  const entityId = useCurrentEntityId();
  const { successToast, alertToast } = useToast();
  const privewModal = useModal();
  const { isAdmin } = useRoleBasedView();
  const [previewFile, setPreviewFile] = useState<File>();

  const {
    data: registrationDetailsData,
    isLoading,
    isSuccess,
    isFetching,
    refetch,
  } = useGetSalesTaxRegistrationDetailsQuery(
    {
      entityId,
      state_registration_id:
        initialRegistrationData?.state_registration_id || "",
    },
    {
      skip: !initialRegistrationData?.state_registration_id,
      refetchOnMountOrArgChange: true,
    }
  );

  const [createSalesTaxRegistration, { isLoading: isCreatingRegistration }] =
    useCreatePostSalesRegistrationsMutation();

  const [
    updatePostSalesTaxRegistration,
    { isLoading: isUpdatingRegistration },
  ] = useUpdatePostSalesTaxRegistrationsMutation();

  const handleSubmit = async (values: FormikValues) => {
    const payment_schedule = values.payment_schedule?.length
      ? values.payment_schedule
      : NONE;

    try {
      if (initialRegistrationData?.state_registration_id) {
        await updatePostSalesTaxRegistration({
          entityId,
          state_registration_id:
            initialRegistrationData?.state_registration_id as string,
          payload: {
            ...values,
            payment_schedule,
          } as PostSalesRegistrationPayload,
        }).unwrap();
        successToast({
          message: `Registration ${
            isAdmin ? `details updated` : `completed`
          } for ${registrationDetailsData?.state}`,
        });
        refetch();
      } else {
        const { state_registration_id, ...restValues } = values;
        await createSalesTaxRegistration({
          entityId,
          payload: {
            ...restValues,
            payment_schedule,
          } as PostSalesRegistrationPayload,
        }).unwrap();
        successToast({
          message: `Registration completed for ${getSelectedState(
            values.state_id,
            stateOptions
          )}`,
        });
      }
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const isRegistrationInProcess =
    registrationDetailsData?.status === PROCESSING;

  const payment_schedule = registrationDetailsData?.payment_schedule;
  const initialValues = {
    tax_detail_id: registrationDetailsData?.tax_detail_id ?? "",
    state_id: initialRegistrationData?.state_id ?? "",
    payment_schedule:
      payment_schedule && payment_schedule !== NONE ? payment_schedule : "",
    filing_start_date: registrationDetailsData?.filing_start_date || "",
    sales_tax_id: registrationDetailsData?.sales_tax_id ?? "",
    existingTaxAccount: isRegistrationInProcess ? "NO" : "YES",
    state_registration_id: initialRegistrationData?.state_registration_id || "",
    registration_license: registrationDetailsData?.registration_license
      ?.file_name
      ? registrationDetailsData?.registration_license
      : null,
    application: registrationDetailsData?.application?.file_id
      ? registrationDetailsData?.application
      : null,
  };
  const { uuid: groupId } = useCurrentGroupContext();

  const onPreview = async ({
    pdfFile,
    uuid,
  }: {
    pdfFile: any;
    uuid?: string;
  }) => {
    if (uuid) {
      uuidBasedPreview({ fileId: uuid });
      return;
    }
    try {
      setPreviewFile(pdfFile);
      privewModal.open();
    } catch (error: any) {
      alertToast({ message: error });
    }
  };
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const uuidBasedPreview = async ({ fileId }: { fileId: string }) => {
    try {
      const { preview_url } = await getPreviewUrl({ groupId, fileId }).unwrap();
      openLink(preview_url);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open onOpenChange={onClose}>
      <Modal.Content>
        <Async.Root
          isLoading={isLoading || isFetching}
          isSuccess={
            !initialRegistrationData?.state_registration_id || isSuccess
          }
          isEmpty={Boolean(
            initialRegistrationData?.state_registration_id &&
              !registrationDetailsData
          )}
        >
          {previewFile && (
            <LocalFilePreview
              isOpen={privewModal.isOpen}
              file={previewFile}
              close={privewModal.close}
            />
          )}
          <Async.Success>
            <FormikForm
              initialValues={{
                ...initialValues,
              }}
              validationSchema={
                isAdmin
                  ? addRegistrationAdminSchema
                  : addExistingRegistrationValidation
              }
              onSubmit={handleSubmit}
            >
              {({ setFieldValue, values }) => {
                return (
                  <>
                    <Modal.Header>
                      <Modal.Title>
                        {initialRegistrationData?.state_registration_id
                          ? `Registration details`
                          : "Add Existing Registration"}{" "}
                        {initialRegistrationData?.state &&
                          `(${initialRegistrationData?.state})`}
                      </Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-max-h-[60vh] t-overflow-y-scroll">
                      <div className="t-flex t-gap-y-3 t-flex-col ">
                        {!initialRegistrationData?.state_registration_id &&
                          !initialRegistrationData?.state_id && (
                            <Combobox
                              withForm
                              size="regular"
                              label="State"
                              name={`state_id`}
                              required
                              options={stateOptions}
                              isClearable={false}
                            />
                          )}

                        <TextInput
                          label="Sales Tax ID"
                          placeholder="xxx-xx-xxxx"
                          name="sales_tax_id"
                          defaultValue={values.sales_tax_id}
                          required={!isAdmin}
                        />
                        <Combobox
                          withForm
                          size="regular"
                          label="Payment schedule"
                          name={`payment_schedule`}
                          defaultValue={
                            payment_schedule && payment_schedule !== NONE
                              ? {
                                  label:
                                    paymentScheduleMap[
                                      registrationDetailsData?.payment_schedule
                                    ],
                                  value:
                                    registrationDetailsData?.payment_schedule,
                                }
                              : null
                          }
                          required={!isAdmin}
                          options={paymentScheduleOption}
                          isClearable={false}
                        />

                        {isAdmin && (
                          <Combobox
                            withForm
                            size="regular"
                            label="Registration status"
                            name={`status`}
                            defaultValue={
                              registrationDetailsData?.status
                                ? {
                                    label:
                                      StatusMap[
                                        registrationDetailsData?.status
                                      ],
                                    value: registrationDetailsData?.status,
                                  }
                                : {
                                    label: StatusMap[PENDING_DETAILS],
                                    value: PENDING_DETAILS,
                                  }
                            }
                            required={!isAdmin}
                            options={statusOptions}
                            isClearable={false}
                          />
                        )}

                        <Field name="filing_start_date">
                          {({ field }: FieldProps) => {
                            return (
                              <DateInput
                                {...field}
                                required={!isAdmin}
                                label={
                                  <span className="t-inline-flex t-gap-x-1 t-items-center">
                                    Filing service start date{" "}
                                    <ToolTip
                                      text={
                                        <span>
                                          Pick the first day of the tax period
                                          you want us <br />
                                          to handle (e.g., April 1 for Q2
                                          filing)
                                        </span>
                                      }
                                    >
                                      <div className="-t-mt-0.5">
                                        <ToolTipIcon />
                                      </div>
                                    </ToolTip>
                                  </span>
                                }
                                placeholder={DISPLAY_DATE}
                                disabled={isRegistrationInProcess}
                                portalId="validOnDate"
                              />
                            );
                          }}
                        </Field>
                        {Boolean(
                          (!registrationDetailsData?.sales_tax_id &&
                            !isRegistrationInProcess) ||
                            isAdmin
                        ) && (
                          <div>
                            <p className="t-text-text-30 t-text-body-sm t-mb-3 t-flex t-items-center t-gap-1">
                              Do you have existing tax account ID in this state?{" "}
                              <ToolTip
                                text={
                                  <span>
                                    e.g. Franchise Tax ID, Withholding <br />
                                    Tax ID, SOS ID, Payroll Tax ID, etc.
                                  </span>
                                }
                              >
                                <div className="-t-mt-0.5">
                                  <ToolTipIcon />
                                </div>
                              </ToolTip>
                            </p>

                            <RadioGroup.Root
                              onValueChange={(value) => {
                                setFieldValue("existingTaxAccount", value);
                              }}
                              className="t-flex t-flex-row t-gap-3 t-text-body"
                              defaultValue={values?.existingTaxAccount}
                            >
                              <RadioGroup.Item
                                value="YES"
                                className="t-max-w-14"
                              >
                                Yes
                              </RadioGroup.Item>
                              <RadioGroup.Item value="NO">No</RadioGroup.Item>
                            </RadioGroup.Root>
                          </div>
                        )}
                        {values.existingTaxAccount === "YES" &&
                          (!registrationDetailsData?.sales_tax_id ||
                            isAdmin) && (
                            <TextInput
                              required={!isAdmin}
                              label="Existing tax accounts ID in this state"
                              note={
                                !isRegistrationInProcess && (
                                  <span className="t-text-neutral-40">
                                    e.g. EIN, SOS ID, Reseller Certificate
                                    Number
                                  </span>
                                )
                              }
                              disabled={isRegistrationInProcess}
                              name="tax_detail_id"
                            />
                          )}
                        <FileInput
                          name="application"
                          withForm
                          label="Registration application summary"
                          file={
                            values?.application?.file_id
                              ? {
                                  uuid: values?.application?.file_id,
                                  name: values?.application?.file_name,

                                  file_type: "PDF",
                                  is_previewable: true,
                                }
                              : null
                          }
                          onFileClick={({ uuid }) => {
                            onPreview({
                              pdfFile: values.application,
                              uuid,
                            });
                          }}
                          onDelete={() => {}}
                          accept={{
                            "application/pdf": [".pdf"],
                          }}
                        />
                        <FileInput
                          name="registration_license"
                          required={!isAdmin}
                          withForm
                          file={
                            values?.registration_license?.file_id
                              ? {
                                  uuid: values?.registration_license?.file_id!,
                                  name: values?.registration_license
                                    ?.file_name!,
                                  file_type: "PDF",
                                  is_previewable: true,
                                }
                              : null
                          }
                          onFileClick={({ uuid }) => {
                            onPreview({
                              pdfFile: values.registration_license,
                              uuid,
                            });
                          }}
                          label="Sales Tax registration license"
                          onDelete={() => {}}
                          accept={{
                            "application/pdf": [".pdf"],
                          }}
                        />
                        {isRegistrationInProcess && (
                          <p className="t-text-text-30 t-flex t-gap-x-2 t-items-start t-text-body-sm t-m-0">
                            <ToolTipIcon />
                            Your Sales Tax license will be sent to your mailing
                            address upon successful <br /> registration. This
                            usually takes 1-2 weeks.
                          </p>
                        )}
                      </div>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Button onClick={onClose}>Cancel</Button>
                      <Button
                        type="submit"
                        isLoading={
                          isUpdatingRegistration || isCreatingRegistration
                        }
                        customType="primary"
                      >
                        {initialRegistrationData?.state_registration_id
                          ? "Update"
                          : "Add"}
                      </Button>
                    </Modal.FooterButtonGroup>
                  </>
                );
              }}
            </FormikForm>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
